import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { NativePdfPreview } from "@/components/documents/NativePdfPreview";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { renderMergeFields, createMergeData } from "@/lib/documentMerge";
import { sanitizeDocumentContent } from "@/lib/sanitize";
import { getDocumentSignedUrl } from "@/lib/storageUtils";

type GovernanceDocumentRow = {
  id: string;
  document_title: string;
  document_type: string;
  document_category: string;
  status: string | null;
  effective_date: string | null;
  created_at: string;
  signed_file_url: string | null;
  original_file_url: string | null;
  content_html: string | null;
  requires_signatures: boolean | null;
  all_signatures_collected: boolean | null;
  signed_by_name: string | null;
  signed_at: string | null;
  resolution_id: string | null;
};

type ResolutionRow = {
  id: string;
  resolution_title: string;
  resolution_number: string | null;
  resolution_date: string | null;
  status: string | null;
  filed_date: string | null;
  whereas_clauses: string[] | null;
  resolved_clauses: string[] | null;
};

const toTitleCase = (value: string | null | undefined) => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export default function GovernanceDocumentDetail() {
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ["governance-document", documentId],
    enabled: !!documentId,
    queryFn: async () => {
      const { data: doc, error: docError } = await supabase
        .from("governance_documents")
        .select("*, unas(name)")
        .eq("id", documentId)
        .single();

      if (docError) throw docError;

      let resolution: ResolutionRow | null = null;
      if ((doc as GovernanceDocumentRow & { unas: { name: string } | null }).resolution_id) {
        const { data: res, error: resError } = await supabase
          .from("una_resolutions")
          .select(
            "id, resolution_title, resolution_number, resolution_date, status, filed_date, whereas_clauses, resolved_clauses"
          )
          .eq("id", (doc as GovernanceDocumentRow).resolution_id)
          .single();

        if (!resError && res) resolution = res as ResolutionRow;
      }

      const unaName = (doc as any).unas?.name || "";
      return { doc: doc as GovernanceDocumentRow, resolution, unaName };
    },
  });

  const signatureStatus = useMemo(() => {
    if (!data?.doc) return "";
    if (data.doc.requires_signatures) {
      return data.doc.all_signatures_collected ? "Fully Signed" : "Pending Signatures";
    }
    return toTitleCase(data.doc.status) || "Filed";
  }, [data?.doc]);

  const pdfPath = data?.doc?.signed_file_url || data?.doc?.original_file_url || null;

  const renderedContentHtml = useMemo(() => {
    const raw = data?.doc?.content_html || "";
    if (!raw) return "";

    let processed = raw;

    // If a full HTML document was stored, extract only body content - let ecclesiastical-document class handle styling
    if (raw.includes("<html") || raw.includes("<body")) {
      try {
        if (typeof DOMParser === "undefined") return raw;
        const doc = new DOMParser().parseFromString(raw, "text/html");
        // Only extract body content to avoid style conflicts
        processed = doc.body?.innerHTML?.trim() || raw;
      } catch {
        processed = raw;
      }
    }

    // Defensive: render any remaining merge fields with UNA name
    if (data?.unaName && (processed.includes("{{") || processed.includes("{{"))) {
      const mergeData = createMergeData(data.unaName);
      processed = renderMergeFields(processed, mergeData);
    }

    return processed;
  }, [data?.doc?.content_html, data?.unaName]);

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4 max-w-5xl">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => navigate("/governance-documents")}> 
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {pdfPath && (
            <Button variant="outline" onClick={async () => {
              const url = await getDocumentSignedUrl({ signed_file_url: pdfPath });
              if (url) window.open(url, '_blank');
            }}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading document...
          </div>
        ) : error || !data?.doc ? (
          <Card>
            <CardHeader>
              <CardTitle>Document not found</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">This document may have been removed or you may not have access.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">{data.doc.document_title}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Category:</span> {data.doc.document_category}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span> {data.doc.document_type}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Status:</span> {signatureStatus}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Added:</span>{" "}
                    {format(new Date(data.doc.created_at), "MMM d, yyyy 'at' h:mm a")}
                  </div>
                  {data.doc.effective_date && (
                    <div>
                      <span className="text-muted-foreground">Effective:</span>{" "}
                      {format(new Date(data.doc.effective_date), "MMMM d, yyyy")}
                    </div>
                  )}
                  {data.doc.signed_by_name && data.doc.signed_at && (
                    <div>
                      <span className="text-muted-foreground">Signed:</span> {data.doc.signed_by_name} on{" "}
                      {format(new Date(data.doc.signed_at), "MMM d, yyyy")}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {pdfPath ? (
              <Card>
                <CardHeader>
                  <CardTitle>PDF Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <NativePdfPreview url={pdfPath} heightClassName="h-[75vh]" fallbackHtml={renderedContentHtml} />
                </CardContent>
              </Card>
            ) : data.doc.content_html ? (
              <Card>
                <CardHeader>
                  <CardTitle>Document Content</CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[75vh]">
                    <div
                      className="p-6 ecclesiastical-document max-w-none"
                      dangerouslySetInnerHTML={{ __html: sanitizeDocumentContent(renderedContentHtml) }}
                    />
                  </ScrollArea>
                </CardContent>
              </Card>
            ) : data.resolution ? (
              <Card>
                <CardHeader>
                  <CardTitle>Resolution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="font-semibold">{data.resolution.resolution_title}</div>
                      {data.resolution.resolution_number && (
                        <div className="text-sm text-muted-foreground">{data.resolution.resolution_number}</div>
                      )}
                    </div>

                    {data.resolution.whereas_clauses?.length ? (
                      <div>
                        <div className="font-medium mb-2">WHEREAS</div>
                        <ol className="list-decimal pl-5 space-y-2">
                          {data.resolution.whereas_clauses.map((c, idx) => (
                            <li key={idx} className="text-sm">{c}</li>
                          ))}
                        </ol>
                      </div>
                    ) : null}

                    {data.resolution.resolved_clauses?.length ? (
                      <div>
                        <div className="font-medium mb-2">RESOLVED</div>
                        <ol className="list-decimal pl-5 space-y-2">
                          {data.resolution.resolved_clauses.map((c, idx) => (
                            <li key={idx} className="text-sm">{c}</li>
                          ))}
                        </ol>
                      </div>
                    ) : null}

                    <div className="text-sm text-muted-foreground">
                      Status: {toTitleCase(data.resolution.status) || "Filed"}
                      {data.resolution.filed_date ? ` • Filed ${format(new Date(data.resolution.filed_date), "MMM d, yyyy")}` : ""}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle>No preview available</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">This record does not have a PDF or stored content yet.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
